/**
 * The status flags on a person in voice: every state that holds shows at
 * once, in one fixed order, and a moderator's server mute or deafen never
 * looks like a self mute (live bug 2026-10-28: one icon slot showed the
 * first state only, so a server deafen on top of a server mute showed
 * nothing, and a server mute looked like a self mute).
 *
 * Hub only (2026-10-28): the sidebar's voice rows ([role icon] [avatar] name
 * [flags]), the voice room's tiles, the member list and the profile card
 * draw them. Whisper and channel commander are never granted in Hub, so
 * neither has a flag. "Can't speak here" (`noTalk`) is the Speak permission
 * denied in that channel (talk power is retired in Hub:
 * docs/decisions/2026-10-28-talk-power-retired.md).
 *
 * Colours (owner, 2026-10-28; Discord's rule): red means a moderator did it
 * (a server or a channel mute or deafen; a shield or a channel mark in the
 * icon says which), gray means the person did it themselves, and "Mute for
 * me" is your own doing, gray too; "can't speak here" stays amber.
 *
 * Pure: the screens feed it what the live roster (comms-voice) and the
 * gateway's voice state say; tests cover every combination.
 */
import type { VoiceMember, VoiceState } from '@neosocial/protocol';
import type { IconName } from '../ui/icons';

export type VoiceFlagKind =
  | 'server-deaf'
  | 'server-mute'
  | 'channel-deaf'
  | 'channel-mute'
  | 'self-deaf'
  | 'self-mute'
  | 'local-mute'
  | 'no-talk';

/**
 * The order flags show in: what a moderator did first (server-wide, then in
 * this channel), then the person's own, then yours.
 */
export const VOICE_FLAG_ORDER: readonly VoiceFlagKind[] = [
  'server-deaf',
  'server-mute',
  'channel-deaf',
  'channel-mute',
  'self-deaf',
  'self-mute',
  'local-mute',
  'no-talk',
];

/** Every state a person in voice can be in at once. */
export interface VoiceFlagState {
  /**
   * A moderator deafened them on the server: they hear nobody and nobody
   * hears them (Discord's Deafen Members). Its own flag: never also shown
   * as a server mute or "can't speak here".
   */
  serverDeaf: boolean;
  /** A moderator muted them on the server: nobody hears them. */
  serverMute: boolean;
  /**
   * A moderator deafened them in the channel they are in now (2026-10-28):
   * there they hear nobody and nobody hears them; anywhere else they are
   * normal.
   */
  channelDeaf: boolean;
  /** A moderator muted them in the channel they are in now: nobody there hears them. */
  channelMute: boolean;
  /** They turned their sound off. */
  selfDeaf: boolean;
  /** They turned their microphone off, set apart from deafening. */
  selfMute: boolean;
  /** You muted them for yourself only. */
  localMute: boolean;
  /**
   * They can't speak in this channel: the Speak permission is denied
   * there. Not a moderator's mute: that is `serverMute`.
   */
  noTalk: boolean;
}

/**
 * The tone a flag is drawn in (a class name on the screens), the owner's
 * rule (2026-10-28): `red` a moderator's doing (server or channel), `gray`
 * the person's own (and your own "Mute for me"), `amber` the channel's
 * Speak deny.
 */
export type VoiceFlagTone = 'red' | 'gray' | 'amber';

/** Who set a flag: a moderator on the whole server, a moderator in this channel, or nobody else. */
export type VoiceFlagBy = 'server' | 'channel' | null;

export interface VoiceFlag {
  kind: VoiceFlagKind;
  icon: IconName;
  /** The i18n key of its tooltip and accessible name (each flag its own). */
  label: string;
  tone: VoiceFlagTone;
  /** A moderator's server mute or deafen: drawn apart from the person's own states. */
  server: boolean;
  /** A moderator's channel mute or deafen (2026-10-28): red too, with a channel mark. */
  channel: boolean;
}

const SPEC: Record<
  VoiceFlagKind,
  { on: keyof VoiceFlagState; icon: IconName; label: string; tone: VoiceFlagTone; by: VoiceFlagBy }
> = {
  'server-deaf': {
    on: 'serverDeaf',
    icon: 'headphones-off-mod',
    label: 'um.voice.flag.serverDeaf',
    tone: 'red',
    by: 'server',
  },
  'server-mute': {
    on: 'serverMute',
    icon: 'mic-off-mod',
    label: 'um.voice.flag.serverMute',
    tone: 'red',
    by: 'server',
  },
  'channel-deaf': {
    on: 'channelDeaf',
    icon: 'headphones-off-ch',
    label: 'um.voice.flag.channelDeaf',
    tone: 'red',
    by: 'channel',
  },
  'channel-mute': {
    on: 'channelMute',
    icon: 'mic-off-ch',
    label: 'um.voice.flag.channelMute',
    tone: 'red',
    by: 'channel',
  },
  'self-deaf': {
    on: 'selfDeaf',
    icon: 'headphones-off',
    label: 'um.voice.flag.selfDeaf',
    tone: 'gray',
    by: null,
  },
  'self-mute': {
    on: 'selfMute',
    icon: 'mic-off',
    label: 'um.voice.flag.selfMute',
    tone: 'gray',
    by: null,
  },
  'local-mute': {
    on: 'localMute',
    icon: 'volume-x',
    label: 'um.voice.flag.localMute',
    tone: 'gray',
    by: null,
  },
  'no-talk': {
    on: 'noTalk',
    icon: 'hand',
    label: 'um.voice.flag.noTalk',
    tone: 'amber',
    by: null,
  },
};

/** The i18n keys the flags use (the strings test checks them in EN and FA). */
export const VOICE_FLAG_KEYS: readonly string[] = VOICE_FLAG_ORDER.map((k) => SPEC[k].label);

/** The flags that hold, in `VOICE_FLAG_ORDER`. */
export function voiceFlags(s: Partial<VoiceFlagState>): VoiceFlag[] {
  return VOICE_FLAG_ORDER.filter((k) => !!s[SPEC[k].on]).map((kind) => ({
    kind,
    icon: SPEC[kind].icon,
    label: SPEC[kind].label,
    tone: SPEC[kind].tone,
    server: SPEC[kind].by === 'server',
    channel: SPEC[kind].by === 'channel',
  }));
}

/**
 * At most this many flags as icons in a narrow row (the sidebar's voice
 * rows, the member list), so the name keeps its room. QA 2026-10-28 (channel
 * mute round 2): with the two channel flags, 5-7 flags left the name 0-27px
 * wide and pushed the last icon past the sidebar's edge.
 */
export const ROW_FLAG_MAX = 4;

/**
 * The next `max` for a row whose flags (`count`) did not fit at `max`: one
 * item fewer than it drew (`condenseFlags` draws `min(count, max)` items: icons,
 * or icons and the "+N" chip), down to the chip alone (1). QA 2026-10-28.
 */
export function nextRowFlagMax(count: number, max: number): number {
  return Math.max(1, Math.min(count, max) - 1);
}

/** Red (a moderator's) outranks amber (can't speak here), which outranks gray. */
const TONE_RANK: Record<VoiceFlagTone, number> = { gray: 0, amber: 1, red: 2 };

/**
 * The flags a narrow row draws: all of them while they fit in `max`, else the
 * first `max - 1` (a moderator's come first, `VOICE_FLAG_ORDER`) and a "+N"
 * chip for the rest, in the strongest tone among them (a moderator's flag
 * never hides behind a gray chip). Every flag stays reachable: the chip's
 * name and tooltip list the rest, and the tile and profile card draw all.
 */
export function condenseFlags(
  flags: VoiceFlag[],
  max: number = ROW_FLAG_MAX,
): { shown: VoiceFlag[]; rest: VoiceFlag[]; tone: VoiceFlagTone | null } {
  if (flags.length <= max) return { shown: flags, rest: [], tone: null };
  const keep = Math.max(0, max - 1);
  const rest = flags.slice(keep);
  const tone = rest.reduce<VoiceFlagTone>(
    (t, f) => (TONE_RANK[f.tone] > TONE_RANK[t] ? f.tone : t),
    'gray',
  );
  return { shown: flags.slice(0, keep), rest, tone };
}

/** What the live roster says about one person. */
export type RosterFlags = Pick<
  VoiceMember,
  'self_mute' | 'self_deaf' | 'mute' | 'deaf' | 'server_mute' | 'channel_mute' | 'channel_deaf'
>;
/**
 * What the gateway's voice state says about one person. `suppress`: they
 * can't speak in their channel (Speak denied there), sent by the server for
 * viewers who are not in voice (no roster to read). `nx_channel_mute` /
 * `nx_channel_deaf`: a moderator's channel mute or deafen where they are.
 */
export type StateFlags = Pick<VoiceState, 'self_mute' | 'self_deaf' | 'mute' | 'deaf'> &
  Partial<Pick<VoiceState, 'suppress' | 'nx_channel_mute' | 'nx_channel_deaf'>>;

/**
 * One person's flags from what this client knows:
 * - `roster`: comms-voice's live roster entry (while you are connected). Its
 *   `mute` covers a server mute and "can't speak here"; `server_mute` tells
 *   them apart (an older comms-voice without it: the gateway's `mute` does).
 * - `state`: the gateway's voice state (always, for people you can see).
 *   Without the roster its `suppress` says "can't speak here" (live bug
 *   2026-10-28: viewers outside voice never saw that flag).
 * - `me`: your own toggles, which the session knows first.
 *
 * "Can't speak here" is shown only when no moderator's server mute holds
 * (that one says more), the same from either source. A channel mute or
 * deafen is its own flag (the roster's `channel_mute` / `channel_deaf`, else
 * the gateway's `nx_channel_mute` / `nx_channel_deaf`), never part of
 * `mute`, so it stacks with every other one.
 */
export function flagStateOf(o: {
  roster?: RosterFlags | null;
  state?: StateFlags | null;
  me?: { selfMute: boolean; selfDeaf: boolean } | null;
  localMute?: boolean;
}): VoiceFlagState {
  const { roster, state, me } = o;
  const serverMute = roster
    ? (roster.server_mute ?? (roster.mute && !!state?.mute))
    : !!state?.mute;
  return {
    serverDeaf: roster ? roster.deaf : !!state?.deaf,
    serverMute,
    // an older comms-voice says nothing of it: the gateway does
    channelDeaf: roster?.channel_deaf ?? !!state?.nx_channel_deaf,
    channelMute: roster?.channel_mute ?? !!state?.nx_channel_mute,
    selfDeaf: me ? me.selfDeaf : roster ? roster.self_deaf : !!state?.self_deaf,
    selfMute: me ? me.selfMute : roster ? roster.self_mute : !!state?.self_mute,
    localMute: !me && !!o.localMute,
    noTalk: (roster ? roster.mute : !!state?.suppress) && !serverMute,
  };
}

/** A moderator's blocks on you, and the channel's, as your own buttons and lines read them. */
export interface OwnModFlags {
  serverMute: boolean;
  serverDeaf: boolean;
  /** In the channel you are in now (2026-10-28). */
  channelMute: boolean;
  channelDeaf: boolean;
  noTalk: boolean;
}

/**
 * Your own server-side state, for your dock and bars: what comms-voice's
 * roster says about you (it tells you at once), else the gateway's state.
 */
export function ownServerFlags(
  roster: RosterFlags | null | undefined,
  state: StateFlags | null | undefined,
): OwnModFlags {
  const f = flagStateOf({ roster, state });
  return {
    serverMute: f.serverMute,
    serverDeaf: f.serverDeaf,
    channelMute: f.channelMute,
    channelDeaf: f.channelDeaf,
    noTalk: f.noTalk,
  };
}

/**
 * Which of a moderator's server mute and deafen on you, you can undo
 * yourself: you hold Mute / Deafen Members in your channel, so your own
 * menus offer "Server unmute" / "Server undeafen" (`memberActions` on
 * yourself; QA 2026-10-28 follow-up round 3). The words then say so,
 * never "until a moderator unmutes you". The same permission there lifts
 * a channel mute / deafen in that channel ("Channel unmute" /
 * "Channel undeafen"), so `mute` / `deaf` cover those too.
 */
export interface SelfLift {
  mute: boolean;
  deaf: boolean;
}

/**
 * The i18n key for the line that says a moderator muted and/or deafened you
 * (null when neither); with `lift`, that you can undo it yourself.
 */
export function serverStatusKey(
  f: { serverMute: boolean; serverDeaf: boolean },
  lift: SelfLift = { mute: false, deaf: false },
): string | null {
  if (f.serverMute && f.serverDeaf) {
    if (lift.mute && lift.deaf) return 'um.voice.serverBothSelf';
    if (lift.mute) return 'um.voice.serverBothSelfMute';
    if (lift.deaf) return 'um.voice.serverBothSelfDeaf';
    return 'um.voice.serverBoth';
  }
  if (f.serverMute) return lift.mute ? 'um.voice.serverMutedSelf' : 'um.voice.serverMuted';
  if (f.serverDeaf) return lift.deaf ? 'um.voice.serverDeafenedSelf' : 'um.voice.serverDeafened';
  return null;
}

/** The keys `serverStatusKey` builds (the strings test checks them in EN and FA). */
export const SERVER_STATUS_KEYS: readonly string[] = [
  'um.voice.serverMuted',
  'um.voice.serverDeafened',
  'um.voice.serverBoth',
  'um.voice.serverMutedSelf',
  'um.voice.serverDeafenedSelf',
  'um.voice.serverBothSelf',
  'um.voice.serverBothSelfMute',
  'um.voice.serverBothSelfDeaf',
];

/**
 * The line that says a moderator muted and/or deafened you in this channel
 * (2026-10-28; null when neither): the same kind of line as the server's,
 * shown beside it when both hold; with `lift`, that you can undo it
 * yourself (the same Mute / Deafen Members in your channel).
 */
export function channelStatusKey(
  f: { channelMute: boolean; channelDeaf: boolean },
  lift: SelfLift = { mute: false, deaf: false },
): string | null {
  if (f.channelMute && f.channelDeaf) {
    if (lift.mute && lift.deaf) return 'um.voice.channelBothSelf';
    if (lift.mute) return 'um.voice.channelBothSelfMute';
    if (lift.deaf) return 'um.voice.channelBothSelfDeaf';
    return 'um.voice.channelBoth';
  }
  if (f.channelMute) return lift.mute ? 'um.voice.channelMutedSelf' : 'um.voice.channelMuted';
  if (f.channelDeaf) return lift.deaf ? 'um.voice.channelDeafenedSelf' : 'um.voice.channelDeafened';
  return null;
}

/** The keys `channelStatusKey` builds (the strings test checks them in EN and FA). */
export const CHANNEL_STATUS_KEYS: readonly string[] = [
  'um.voice.channelMuted',
  'um.voice.channelDeafened',
  'um.voice.channelBoth',
  'um.voice.channelMutedSelf',
  'um.voice.channelDeafenedSelf',
  'um.voice.channelBothSelf',
  'um.voice.channelBothSelfMute',
  'um.voice.channelBothSelfDeaf',
];

/* ---------------- your own mic and sound buttons ---------------- */

/**
 * What keeps you silent (or deaf) whatever you toggle: a moderator on the
 * server, a moderator in this channel, or the channel's Speak deny.
 */
export type SelfBlock = 'server' | 'channel' | 'no-talk';

/**
 * What silences you right now, as a short line (the push-to-talk chip while
 * you hold the key), in `micToggleView`'s order: a server mute, a server
 * deafen (no-speak too), a channel mute, a channel deafen, the channel's
 * Speak deny. Null when nothing does. Review 2026-10-28: the chip said
 * "Talking" while a moderator's mute dropped every frame.
 */
export function ownBlockLine(o: OwnModFlags): { block: SelfBlock; key: string } | null {
  if (o.serverMute) return { block: 'server', key: 'um.voice.serverMuted' };
  if (o.serverDeaf) return { block: 'server', key: 'um.voice.serverDeafened' };
  if (o.channelMute) return { block: 'channel', key: 'um.voice.channelMuted' };
  if (o.channelDeaf) return { block: 'channel', key: 'um.voice.channelDeafened' };
  if (o.noTalk) return { block: 'no-talk', key: 'um.voice.flag.noTalk' };
  return null;
}

/**
 * How your own microphone or sound button shows (the dock and the voice
 * room). Live bug 2026-10-28: while a moderator's mute or deafen
 * held, the button showed only that, so clicking it changed nothing on
 * screen while your own toggle flipped, and after the undo you were still
 * muted by a toggle you never saw turn on.
 *
 * - `pressed` (aria-pressed), the icon and the `off` look follow your own
 *   toggle alone, so every click changes what the button shows. Your own
 *   off state is neutral (gray, owner 2026-10-28: your own doing).
 * - `block` turns the button red when a moderator holds it (server or
 *   channel), with a badge saying which (shield, channel mark), never
 *   instead of your state; the channel's Speak deny is an amber badge.
 * - The accessible name stays the button's own (`name`), with the block's
 *   words after it while one holds; it never flips with `pressed` (a
 *   toggle's label must not change with its state, WAI-ARIA).
 * - `tip` (the visual tooltip) says the action, or the block and your own
 *   state together.
 */
export interface SelfToggleView {
  pressed: boolean;
  icon: IconName;
  block: SelfBlock | null;
  /** i18n key of the accessible name (fixed). */
  name: string;
  /** i18n key of the block's words, added to the name while it holds. */
  blockKey: string | null;
  /** i18n key of the tooltip. */
  tip: string;
}

/** The words a place uses for its own buttons: the fixed name, and the actions for the tooltip. */
export interface ToggleWords {
  /** "Mute" / "Deafen": the fixed accessible name, and the tooltip while it is off. */
  name: string;
  /** "Unmute" / "Undeafen": the tooltip while it is on. */
  undo: string;
}

/**
 * Your microphone button. A self deafen turns your microphone off too (a
 * click undeafens). A moderator's server deafen blocks it as well, with the
 * deafen's own words: Discord's Deafen Members means you can't speak or
 * hear (QA 2026-10-28 round 3: a server deafen alone left you heard, and
 * your mic button said nothing). A server mute, when it also holds, is the
 * reason shown (it is about your voice alone, and outlasts the undeafen).
 * A channel mute or deafen (2026-10-28) blocks it the same way, with its own
 * words ("in this channel"); a server-wide block, when one also holds, is
 * the reason shown (it outlasts a move).
 *
 * `lift`: the moderator's block you can undo yourself (`SelfLift`); its
 * words then say so instead of "until a moderator unmutes you".
 */
export function micToggleView(
  o: {
    selfMute: boolean;
    selfDeaf: boolean;
    serverMute: boolean;
    /** A moderator's server deafen (no-speak too). */
    serverDeaf?: boolean;
    /** A moderator's channel mute in the channel you are in. */
    channelMute?: boolean;
    /** A moderator's channel deafen there (no-speak too). */
    channelDeaf?: boolean;
    noTalk: boolean;
    lift?: SelfLift;
  },
  words: ToggleWords,
): SelfToggleView {
  const pressed = o.selfMute || o.selfDeaf;
  type Why = 'srvMute' | 'srvDeaf' | 'chMute' | 'chDeaf' | 'noTalk';
  const why: Why | null = o.serverMute
    ? 'srvMute'
    : o.serverDeaf
      ? 'srvDeaf'
      : o.channelMute
        ? 'chMute'
        : o.channelDeaf
          ? 'chDeaf'
          : o.noTalk
            ? 'noTalk'
            : null;
  const block: SelfBlock | null =
    why === 'noTalk'
      ? 'no-talk'
      : why === 'chMute' || why === 'chDeaf'
        ? 'channel'
        : why
          ? 'server'
          : null;
  // the block shown is one you can undo yourself (a channel mute needs the
  // same Mute Members in your channel as a server mute, and so on)
  const self =
    ((why === 'srvMute' || why === 'chMute') && !!o.lift?.mute) ||
    ((why === 'srvDeaf' || why === 'chDeaf') && !!o.lift?.deaf);
  const own = pressed ? 'MicOff' : 'MicOn';
  const blockKeys: Record<Why, string> = {
    srvMute: 'um.voice.serverMutedTip',
    srvDeaf: 'um.voice.serverDeafenedTip',
    chMute: 'um.voice.channelMutedTip',
    chDeaf: 'um.voice.channelDeafenedTip',
    noTalk: 'um.voice.cantSpeakHere',
  };
  return {
    pressed,
    icon: pressed ? 'mic-off' : 'mic',
    block,
    name: words.name,
    blockKey: why ? `${blockKeys[why]}${self ? 'Self' : ''}` : null,
    tip: why ? `um.voice.tip.${why}${self ? 'Self' : ''}${own}` : pressed ? words.undo : words.name,
  };
}

/**
 * Your sound (deafen) button: a server deafen, else a channel deafen where
 * you are, blocks it. `lift`: you can undo that deafen yourself (`SelfLift`).
 */
export function soundToggleView(
  o: { selfDeaf: boolean; serverDeaf: boolean; channelDeaf?: boolean; lift?: boolean },
  words: ToggleWords,
): SelfToggleView {
  const pressed = o.selfDeaf;
  const block: SelfBlock | null = o.serverDeaf ? 'server' : o.channelDeaf ? 'channel' : null;
  const self = !!block && !!o.lift ? 'Self' : '';
  const sound = pressed ? 'SoundOff' : 'SoundOn';
  return {
    pressed,
    icon: pressed ? 'headphones-off' : 'headphones',
    block,
    name: words.name,
    blockKey:
      block === 'server'
        ? `um.voice.serverDeafenedTip${self}`
        : block === 'channel'
          ? `um.voice.channelDeafenedTip${self}`
          : null,
    tip:
      block === 'server'
        ? `um.voice.tip.srvDeaf${self}${sound}`
        : block === 'channel'
          ? `um.voice.tip.chDeaf${self}${sound}`
          : pressed
            ? words.undo
            : words.name,
  };
}

/** The keys `micToggleView` / `soundToggleView` build (the strings test checks them in EN and FA). */
export const SELF_TOGGLE_KEYS: readonly string[] = [
  'um.voice.tip.srvMuteMicOn',
  'um.voice.tip.srvMuteMicOff',
  'um.voice.tip.noTalkMicOn',
  'um.voice.tip.noTalkMicOff',
  'um.voice.tip.srvDeafMicOn',
  'um.voice.tip.srvDeafMicOff',
  'um.voice.tip.srvDeafSoundOn',
  'um.voice.tip.srvDeafSoundOff',
  'um.voice.tip.chMuteMicOn',
  'um.voice.tip.chMuteMicOff',
  'um.voice.tip.chDeafMicOn',
  'um.voice.tip.chDeafMicOff',
  'um.voice.tip.chDeafSoundOn',
  'um.voice.tip.chDeafSoundOff',
  'um.voice.serverMutedTip',
  'um.voice.serverDeafenedTip',
  'um.voice.channelMutedTip',
  'um.voice.channelDeafenedTip',
  'um.voice.cantSpeakHere',
  // the moderator's block you can undo yourself (`SelfLift`)
  'um.voice.tip.srvMuteSelfMicOn',
  'um.voice.tip.srvMuteSelfMicOff',
  'um.voice.tip.srvDeafSelfMicOn',
  'um.voice.tip.srvDeafSelfMicOff',
  'um.voice.tip.srvDeafSelfSoundOn',
  'um.voice.tip.srvDeafSelfSoundOff',
  'um.voice.serverMutedTipSelf',
  'um.voice.serverDeafenedTipSelf',
  'um.voice.tip.chMuteSelfMicOn',
  'um.voice.tip.chMuteSelfMicOff',
  'um.voice.tip.chDeafSelfMicOn',
  'um.voice.tip.chDeafSelfMicOff',
  'um.voice.tip.chDeafSelfSoundOn',
  'um.voice.tip.chDeafSelfSoundOff',
  'um.voice.channelMutedTipSelf',
  'um.voice.channelDeafenedTipSelf',
];
